/**
 * Conversations Context
 * Provides global state management for conversations across the app
 * Avoids redundant API calls by centralizing conversation data
 */

import React, { createContext, useContext, useCallback, useReducer, useEffect } from 'react';
import { copilotApi, CopilotApiError } from '../services/copilotApi';
import { Conversation, ConversationDetails } from '../types/conversations';
import { useWebSocket } from './WebSocketContext';

// State interface
interface ConversationsState {
  // Conversation list
  conversations: Conversation[];
  conversationsLoading: boolean;
  conversationsError: string | null;
  hasMoreConversations: boolean;
  nextToken?: string;
  
  // Selected conversation details
  selectedConversationId: string | null;
  selectedConversation: ConversationDetails | null;
  selectedConversationLoading: boolean;
  selectedConversationError: string | null;
  
  // Search state
  searchResults: Conversation[];
  searchLoading: boolean;
  searchError: string | null;
  searchQuery: string;
  searchHasMore: boolean;
  searchNextToken?: string;
}

// Action types
type ConversationsAction =
  | { type: 'LOAD_CONVERSATIONS_START' }
  | { type: 'LOAD_CONVERSATIONS_SUCCESS'; payload: { conversations: Conversation[]; hasMore: boolean; nextToken?: string; reset: boolean } }
  | { type: 'LOAD_CONVERSATIONS_ERROR'; payload: string }
  | { type: 'SELECT_CONVERSATION'; payload: string }
  | { type: 'LOAD_SELECTED_CONVERSATION_START' }
  | { type: 'LOAD_SELECTED_CONVERSATION_SUCCESS'; payload: ConversationDetails }
  | { type: 'LOAD_SELECTED_CONVERSATION_ERROR'; payload: string }
  | { type: 'CLEAR_SELECTED_CONVERSATION' }
  | { type: 'SEARCH_CONVERSATIONS_START'; payload: string }
  | { type: 'SEARCH_CONVERSATIONS_SUCCESS'; payload: { conversations: Conversation[]; hasMore: boolean; nextToken?: string; reset: boolean } }
  | { type: 'SEARCH_CONVERSATIONS_ERROR'; payload: string }
  | { type: 'CLEAR_SEARCH' }
  | { type: 'REFRESH_CONVERSATIONS' }
  | { type: 'UPDATE_CONVERSATION_TITLE'; payload: { conversationId: string; title: string } }
  | { type: 'ADD_NEW_CONVERSATION'; payload: Conversation };

// Initial state
const initialState: ConversationsState = {
  conversations: [],
  conversationsLoading: false,
  conversationsError: null,
  hasMoreConversations: false,
  nextToken: undefined,
  
  selectedConversationId: null,
  selectedConversation: null,
  selectedConversationLoading: false,
  selectedConversationError: null,
  
  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchQuery: '',
  searchHasMore: false,
  searchNextToken: undefined,
};

// Reducer
const conversationsReducer = (state: ConversationsState, action: ConversationsAction): ConversationsState => {
  switch (action.type) {
    case 'LOAD_CONVERSATIONS_START':
      return {
        ...state,
        conversationsLoading: true,
        conversationsError: null,
      };
    
    case 'LOAD_CONVERSATIONS_SUCCESS':
      return {
        ...state,
        conversationsLoading: false,
        conversationsError: null,
        conversations: action.payload.reset 
          ? action.payload.conversations 
          : [...state.conversations, ...action.payload.conversations],
        hasMoreConversations: action.payload.hasMore,
        nextToken: action.payload.nextToken,
      };
    
    case 'LOAD_CONVERSATIONS_ERROR':
      return {
        ...state,
        conversationsLoading: false,
        conversationsError: action.payload,
      };
    
    case 'SELECT_CONVERSATION':
      return {
        ...state,
        selectedConversationId: action.payload,
        selectedConversation: null,
        selectedConversationError: null,
      };
    
    case 'LOAD_SELECTED_CONVERSATION_START':
      return {
        ...state,
        selectedConversationLoading: true,
        selectedConversationError: null,
      };
    
    case 'LOAD_SELECTED_CONVERSATION_SUCCESS':
      return {
        ...state,
        selectedConversationLoading: false,
        selectedConversationError: null,
        selectedConversation: action.payload,
      };
    
    case 'LOAD_SELECTED_CONVERSATION_ERROR':
      return {
        ...state,
        selectedConversationLoading: false,
        selectedConversationError: action.payload,
      };
    
    case 'CLEAR_SELECTED_CONVERSATION':
      return {
        ...state,
        selectedConversationId: null,
        selectedConversation: null,
        selectedConversationError: null,
      };
    
    case 'SEARCH_CONVERSATIONS_START':
      return {
        ...state,
        searchLoading: true,
        searchError: null,
        searchQuery: action.payload,
        searchResults: action.payload === '' ? [] : state.searchResults,
        searchHasMore: false,
        searchNextToken: undefined,
      };
    
    case 'SEARCH_CONVERSATIONS_SUCCESS':
      return {
        ...state,
        searchLoading: false,
        searchError: null,
        searchResults: action.payload.reset 
          ? action.payload.conversations 
          : [...state.searchResults, ...action.payload.conversations],
        searchHasMore: action.payload.hasMore,
        searchNextToken: action.payload.nextToken,
      };
    
    case 'SEARCH_CONVERSATIONS_ERROR':
      return {
        ...state,
        searchLoading: false,
        searchError: action.payload,
      };
    
    case 'CLEAR_SEARCH':
      return {
        ...state,
        searchResults: [],
        searchLoading: false,
        searchError: null,
        searchQuery: '',
        searchHasMore: false,
        searchNextToken: undefined,
      };
    
    case 'REFRESH_CONVERSATIONS':
      return {
        ...state,
        nextToken: undefined,
      };
    
    case 'UPDATE_CONVERSATION_TITLE': {
      console.log('ConversationsContext reducer: Processing UPDATE_CONVERSATION_TITLE for', action.payload.conversationId, 'with title:', action.payload.title);
      const updatedState = {
        ...state,
        conversations: state.conversations.map(conv =>
          conv.conversationId === action.payload.conversationId
            ? { ...conv, title: action.payload.title }
            : conv
        ),
        searchResults: state.searchResults.map(conv =>
          conv.conversationId === action.payload.conversationId
            ? { ...conv, title: action.payload.title }
            : conv
        ),
        // Update selected conversation if it matches
        selectedConversation: state.selectedConversation && 
          state.selectedConversation.conversation.conversationId === action.payload.conversationId
          ? {
              ...state.selectedConversation,
              conversation: {
                ...state.selectedConversation.conversation,
                title: action.payload.title
              }
            }
          : state.selectedConversation,
      };
      console.log('ConversationsContext reducer: Title update processed, updated conversations:', updatedState.conversations.length);
      return updatedState;
    }
    
    case 'ADD_NEW_CONVERSATION': {
      // Check if conversation already exists to avoid duplicates
      const conversationExists = state.conversations.some(
        conv => conv.conversationId === action.payload.conversationId
      );
      if (conversationExists) {
        return state;
      }
      
      return {
        ...state,
        conversations: [action.payload, ...state.conversations], // Add to beginning of list
      };
    }
    
    default:
      return state;
  }
};

// Context interface  
interface ConversationsContextValue extends ConversationsState {
  // Actions
  loadConversations: (reset?: boolean) => Promise<void>;
  loadMoreConversations: () => Promise<void>;
  refreshConversations: () => Promise<void>;
  selectConversation: (conversationId: string) => void;
  clearSelectedConversation: () => void;
  searchConversations: (query: string) => Promise<void>;
  loadMoreSearchResults: () => Promise<void>;
  clearSearch: () => void;
  updateConversationTitle: (conversationId: string, title: string) => void;
  addNewConversation: (conversation: Conversation) => void;
}

// Create context
const ConversationsContext = createContext<ConversationsContextValue | null>(null);

// Provider component
interface ConversationsProviderProps {
  children: React.ReactNode;
}

export const ConversationsProvider: React.FC<ConversationsProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(conversationsReducer, initialState);
  
  // Use WebSocket context for conversation events
  const { onConversationCreated, setInsuranceReportId } = useWebSocket();

  // Load conversations
  const loadConversations = useCallback(async (reset = false) => {
    dispatch({ type: 'LOAD_CONVERSATIONS_START' });
    
    try {
      const response = await copilotApi.getConversations({
        nextToken: reset ? undefined : state.nextToken,
        limit: 20,
      });
      
      dispatch({
        type: 'LOAD_CONVERSATIONS_SUCCESS',
        payload: {
          conversations: response.conversations,
          hasMore: response.pagination?.hasMore || false,
          nextToken: response.pagination?.nextToken,
          reset,
        },
      });
    } catch (error) {
      const errorMessage = error instanceof CopilotApiError 
        ? error.message 
        : 'Failed to load conversations';
      dispatch({ type: 'LOAD_CONVERSATIONS_ERROR', payload: errorMessage });
    }
  }, [state.nextToken]);

  // Load more conversations
  const loadMoreConversations = useCallback(async () => {
    if (!state.conversationsLoading && state.hasMoreConversations) {
      await loadConversations(false);
    }
  }, [state.conversationsLoading, state.hasMoreConversations, loadConversations]);

  // Refresh conversations
  const refreshConversations = useCallback(async () => {
    dispatch({ type: 'REFRESH_CONVERSATIONS' });
    await loadConversations(true);
  }, [loadConversations]);

  // Select conversation
  const selectConversation = useCallback(async (conversationId: string) => {
    dispatch({ type: 'SELECT_CONVERSATION', payload: conversationId });
    dispatch({ type: 'LOAD_SELECTED_CONVERSATION_START' });

    try {
      const response = await copilotApi.getConversation(conversationId);
      dispatch({ type: 'LOAD_SELECTED_CONVERSATION_SUCCESS', payload: response });

      // Update insurance report ID in WebSocket context if present
      if (response.conversation.insuranceReportId) {
        setInsuranceReportId(response.conversation.insuranceReportId);
      } else {
        setInsuranceReportId(null);
      }
    } catch (error) {
      const errorMessage = error instanceof CopilotApiError
        ? error.message
        : 'Failed to load conversation';
      dispatch({ type: 'LOAD_SELECTED_CONVERSATION_ERROR', payload: errorMessage });
    }
  }, [setInsuranceReportId]);

  // Clear selected conversation
  const clearSelectedConversation = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTED_CONVERSATION' });
  }, []);

  // Search conversations
  const searchConversations = useCallback(async (query: string) => {
    dispatch({ type: 'SEARCH_CONVERSATIONS_START', payload: query });
    
    if (!query.trim()) {
      dispatch({ type: 'CLEAR_SEARCH' });
      return;
    }
    
    try {
      const response = await copilotApi.searchConversations(query, {
        limit: 20,
      });
      
      dispatch({
        type: 'SEARCH_CONVERSATIONS_SUCCESS',
        payload: {
          conversations: response.conversations,
          hasMore: response.pagination?.hasMore || false,
          nextToken: response.pagination?.nextToken,
          reset: true,
        },
      });
    } catch (error) {
      const errorMessage = error instanceof CopilotApiError 
        ? error.message 
        : 'Failed to search conversations';
      dispatch({ type: 'SEARCH_CONVERSATIONS_ERROR', payload: errorMessage });
    }
  }, []);

  // Load more search results
  const loadMoreSearchResults = useCallback(async () => {
    if (!state.searchLoading && state.searchHasMore && state.searchQuery.trim()) {
      try {
        const response = await copilotApi.searchConversations(state.searchQuery, {
          nextToken: state.searchNextToken,
          limit: 20,
        });
        
        dispatch({
          type: 'SEARCH_CONVERSATIONS_SUCCESS',
          payload: {
            conversations: response.conversations,
            hasMore: response.pagination?.hasMore || false,
            nextToken: response.pagination?.nextToken,
            reset: false,
          },
        });
      } catch (error) {
        const errorMessage = error instanceof CopilotApiError 
          ? error.message 
          : 'Failed to load more search results';
        dispatch({ type: 'SEARCH_CONVERSATIONS_ERROR', payload: errorMessage });
      }
    }
  }, [state.searchLoading, state.searchHasMore, state.searchQuery, state.searchNextToken]);

  // Clear search
  const clearSearch = useCallback(() => {
    dispatch({ type: 'CLEAR_SEARCH' });
  }, []);

  // Update conversation title
  const updateConversationTitle = useCallback((conversationId: string, title: string) => {
    console.log('ConversationsContext: updateConversationTitle called for', conversationId, 'with title:', title);
    dispatch({ 
      type: 'UPDATE_CONVERSATION_TITLE', 
      payload: { conversationId, title } 
    });
    console.log('ConversationsContext: UPDATE_CONVERSATION_TITLE action dispatched');
  }, []);

  // Add new conversation to the list
  const addNewConversation = useCallback((conversation: Conversation) => {
    dispatch({ 
      type: 'ADD_NEW_CONVERSATION', 
      payload: conversation 
    });
  }, []);

  // Auto-load conversations on mount
  useEffect(() => {
    // Use a ref to avoid infinite loop
    let mounted = true;
    
    const initialLoad = async () => {
      dispatch({ type: 'LOAD_CONVERSATIONS_START' });
      
      try {
        const response = await copilotApi.getConversations({
          limit: 20,
        });
        
        if (mounted) {
          dispatch({
            type: 'LOAD_CONVERSATIONS_SUCCESS',
            payload: {
              conversations: response.conversations,
              hasMore: response.pagination?.hasMore || false,
              nextToken: response.pagination?.nextToken,
              reset: true,
            },
          });
        }
      } catch (error) {
        if (mounted) {
          const errorMessage = error instanceof CopilotApiError 
            ? error.message 
            : 'Failed to load conversations';
          dispatch({ type: 'LOAD_CONVERSATIONS_ERROR', payload: errorMessage });
        }
      }
    };
    
    initialLoad();
    
    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array for mount-only effect

  // Set up WebSocket listener for conversation created events
  useEffect(() => {
    const unsubscribe = onConversationCreated((data) => {
      console.log('Conversation created event received in context:', data);
      
      // Add the new conversation to the list
      addNewConversation(data.conversation);
    });
    
    return unsubscribe;
  }, [addNewConversation, onConversationCreated]);

  const contextValue: ConversationsContextValue = {
    ...state,
    loadConversations,
    loadMoreConversations,
    refreshConversations,
    selectConversation,
    clearSelectedConversation,
    searchConversations,
    loadMoreSearchResults,
    clearSearch,
    updateConversationTitle,
    addNewConversation,
  };

  return (
    <ConversationsContext.Provider value={contextValue}>
      {children}
    </ConversationsContext.Provider>
  );
};

// Hook to use the context
export const useConversationsContext = (): ConversationsContextValue => {
  const context = useContext(ConversationsContext);
  if (!context) {
    throw new Error('useConversationsContext must be used within a ConversationsProvider');
  }
  return context;
};
